import {
  OrderType,
  OrderTypeCode,
  DocketItem,
  Docket,
  DocketStatus,
  DocketItemStatus,
  SalesChannel,
  SALES_CHANNEL,
  App,
  PrinterProfile,
  GroupBy,
  DEFAULT_TABLE_ABBREVIATION,
  Category,
  GroupDisplayStyle,
  DocketModifierEntityInput,
} from '@oolio-group/domain';
import theme from '../../common/theme';
import { getDocketItemDisplayName } from './docketItemDisplayName';
import { buildDocketNote } from './docketNote';
import {
  filterOutRemovedCourseItems,
  getRemovableCourseIds,
  getRemovedCourses,
  formatRemovedCourseNames,
  resolveIsCourseCallApplicable,
  groupItemsByCourseId,
  isFullyBumped,
  CourseCompletionPolicy,
} from './courseCompletion';
import {
  DocketDisplayItemType,
  DEFAULT_DOCKET_NAME,
  DocketDisplayOrderItem,
  DocketDisplayItem,
  DocketDisplayBaseItem,
  DocketDisplayOtherItem,
  DocketDisplayComboItem,
  DocketDisplayModifierItem,
  FulfilmentDetails,
} from '../../common/types';
import { Session } from '../../state/preferences';

import {
  DocketDisplayItemTypeHeight,
  RECALL_TEXT,
  CONTINUE,
  VOIDED_TEXT,
  DOCKET_HEADER_COLOR,
  RECALLED_TEXT,
  HeaderHeight,
  FooterHeight,
  TextCharacterCount,
  TextLineHeight,
  ItemNameMaxLines,
  DOCKET_TITLE_BAR,
} from '../../common/constants';
import { removeExtraSpaces } from '../../utils/general';
import { constructNotifierRows } from './notifierRows';
import { partition, groupBy } from 'lodash';
import { translate } from '@oolio-group/localization';
import { capitalizeWords } from '../string';
import {
  formatAllergenList,
  formatAllergenName,
  hasAllergens,
} from '../allergens';

/*
 example
 input-1 @orderNumber 'SP-6-10-20220815' to be formatted to output SP-6-10
 input-2 @orderNumber 'SP-6-10-20220815-1' to be formatted to output SP-6-10-1

 @example2 for online orders format is
 input-1 @orderNumber 'OL-6-20220815' to be formatted to output OL-6
*/

// Matches the partition condition in transformDocketToDocketDisplayItems — deliberately
// itemStatus-agnostic, since a "transferred in" item can carry transferInfo without ever
// getting DocketItemStatus.TRANSFERRED (only the "transferred away" side does).
const hasTransferInfo = (item: DocketItem): boolean =>
  !!item.transferInfo?.fromOrderId && !!item.transferInfo?.toOrderId;

/** Items transferred together in the same action share this key (destination table + who
 * transferred them). Shared between the docket card's grouping and the details drawer's
 * per-item banner suppression so they can't drift apart. Null for a non-transferred item. */
export const getTransferGroupKey = (item: DocketItem): string | null =>
  hasTransferInfo(item)
    ? `${item.transferInfo!.toTableId}#${item.transferInfo!.transferredBy}`
    : null;

/** Resolves a transferred item's banner table id + direction. Shared between the docket
 * card (constructTransferredItems) and the details drawer. Null for a non-transferred item. */
export const resolveTransferLabel = (
  item: DocketItem,
): { tableId: string; transferLabelType: DocketDisplayItemType } | null => {
  if (!hasTransferInfo(item)) {
    return null;
  }
  const { toTableId = '', fromTableId = '', toOrderId } = item.transferInfo!;
  const isTransferredTo = toOrderId !== item.orderId;
  return {
    tableId: (isTransferredTo ? toTableId : fromTableId) || '',
    transferLabelType: isTransferredTo
      ? DocketDisplayItemType.TRANSFERRED_TO_LABEL
      : DocketDisplayItemType.TRANSFERRED_FROM_LABEL,
  };
};

const groupTransfers = (docketItems: DocketItem[]) => {
  return Object.values(
    groupBy(docketItems, item => getTransferGroupKey(item) ?? ''),
  ) as DocketItem[][];
};

export function minifyOnlineOrderNumber(orderNumber: string): string {
  const splitedOrderNumber = orderNumber.split('-');
  const onlineOrderNum: string =
    (splitedOrderNumber?.[0] || '') + '-' + (splitedOrderNumber?.[1] || '');
  return onlineOrderNum;
}

export function minifyPosOrderNumber(orderNumber: string): string {
  const splitedOrderNumber = orderNumber.split('-');
  const prefix: string = splitedOrderNumber[0] || '';
  let result = prefix;

  if (splitedOrderNumber?.length > 1) {
    const secondPart = splitedOrderNumber[1].replace(/\D/g, '') || '';
    const thirdPart = splitedOrderNumber[2] || '';
    const fourthPart = splitedOrderNumber[4] ? `-${splitedOrderNumber[4]}` : '';
    result = `${prefix}${secondPart}-${thirdPart}${fourthPart}`;
  }

  return result;
}

export function minifyOrderNumber(orderNumber: string): string {
  if (orderNumber && orderNumber.includes('OL')) {
    return minifyOnlineOrderNumber(orderNumber);
  } else if (orderNumber) {
    return minifyPosOrderNumber(orderNumber);
  }
  return orderNumber;
}

const calculateDocketTitleBarRows = (
  tags: string[] = [],
  rushedAt?: number,
  docketCount?: string,
  seatedGuests = 0,
): number => {
  if (!tags.length && !rushedAt && !docketCount && !seatedGuests) return 0;

  const hasRushTag = !!rushedAt;
  const emojiCount = tags.length;

  /** Simple thresholds adjusted for docket count
   * These numbers where chosen based on the assumption that
   * each emoji takes up a certain amount of space, and generally
   * we have 7 emojis in a single row without wrapping (with rush tag) and
   * 9 emojis without a rush tag. With docket count, the count badge is fixed
   * on the right side and tags wrap within the remaining space (flex: 1).
   * To reduce unwanted complexity of using DOCKET_WIDTH / EmojiWIDTH,
   * we are using these simple numbers.
   */
  let rows = 1;
  const hasDocketCount = !!docketCount;

  // Thresholds remain the same since tags still wrap at similar points
  // The docket count badge being fixed doesn't change when tags start wrapping
  const rushThreshold = hasDocketCount ? 6 : 7;
  const normalThreshold = hasDocketCount ? 8 : 9;

  if (hasRushTag && emojiCount > rushThreshold) rows = 2;
  if (!hasRushTag && emojiCount > normalThreshold) rows = 2;

  return rows;
};

export const constructDisplayDocketTitleBar = (
  docket: Docket,
  currentIndex: number | undefined,
  totalCount: number | undefined,
): DocketDisplayBaseItem => {
  const docketCount =
    currentIndex !== undefined && totalCount !== undefined && totalCount > 1
      ? `${currentIndex + 1}/${totalCount}`
      : undefined;

  const seatedGuests = docket.seatedGuests ?? 0;

  const numberOfLines = calculateDocketTitleBarRows(
    docket.tags,
    docket.rushedAt,
    docketCount,
    seatedGuests,
  );

  const titleHeader = {
    name: DOCKET_TITLE_BAR,
    height:
      numberOfLines *
      DocketDisplayItemTypeHeight[DocketDisplayItemType.DOCKET_TITLE_BAR],
    type: DocketDisplayItemType.DOCKET_TITLE_BAR,
    docketId: docket.id,
    orderId: docket.orderId,
    tags: docket.tags || [],
    rushedAt: docket.rushedAt,
    docketCount,
    seatedGuests,
  };

  return titleHeader;
};

export const constructDisplayTrainingHeader = (
  docket: Docket,
): DocketDisplayBaseItem => {
  const trainingHeader = {
    type: DocketDisplayItemType.TRAINING_DOCKET,
    name: translate('kitchenDisplay.trainingDocket'),
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.TRAINING_DOCKET],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return trainingHeader;
};

export const constructDisplayDocketHeader = (
  docket: Docket,
): DocketDisplayItem[] => {
  const displayDocket = [];

  const orderInfo = {
    id: docket.id,
    type: DocketDisplayItemType.DOCKET_HEADER,
    name: DEFAULT_DOCKET_NAME,
    orderNumber:
      docket.integrationInfo?.channelOrderDisplayId ||
      minifyOrderNumber(docket.orderNumber),
    orderType: docket?.orderType?.id,
    time: docket.createdAt,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.DOCKET_HEADER],
    section: docket?.section?.name,
    table: docket?.table?.name,
    customer: docket?.customer?.name,
    salesChannel: docket?.salesChannel?.id,
    itemStatus: docket?.itemStatus,
    source: docket?.source,
    status: docket.status,
    isTraining: !!docket.isTraining,
    fulfilmentDetails: docket?.fulfilmentDetails,
    tags: docket.tags || [],
    notifierItems: docket.notifierItems || [],
    rushedAt: docket.rushedAt,
    staffName: docket?.createdBy?.name,
  };
  displayDocket.push(orderInfo);
  return displayDocket;
};

export const constructDisplayDocketNotes = (
  docket: Docket,
): DocketDisplayItem => {
  const tempNotes = removeExtraSpaces(docket.notes || '');
  const shortNotes =
    tempNotes.length > 50 ? tempNotes.slice(0, 50) + '...' : tempNotes;
  const name = buildDocketNote(shortNotes, docket.customFieldResponses);
  const numberOfLines = getNoOfLinesForText(name);
  const orderNote = {
    type: DocketDisplayItemType.ORDER_NOTE,
    name,
    height:
      DocketDisplayItemTypeHeight[DocketDisplayItemType.ORDER_NOTE] +
      (numberOfLines > 1 ? (numberOfLines - 1) * TextLineHeight : 0),
    docketId: docket.id,
    time: docket.createdAt,
  };
  return orderNote;
};

export const getAllergenLabelsHeight = (docketItem: DocketItem): number => {
  const perLabel =
    DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM + 'ALLERGEN'];

  return (docketItem.allergens || []).reduce((total, code) => {
    const lines = getNoOfLinesForText(formatAllergenName(code));

    return total + perLabel + (lines > 1 ? (lines - 1) * TextLineHeight : 0);
  }, 0);
};

export const constructAllergenBanner = (
  docket: Docket,
): DocketDisplayItem | undefined => {
  if (!hasAllergens(docket.allergens)) return undefined;

  const name = translate('kitchenDisplay.allergensLabel', {
    allergens: formatAllergenList(docket.allergens),
  });
  const numberOfLines = getNoOfLinesForText(name);
  const heightAdjustment =
    numberOfLines > 1 ? (numberOfLines - 1) * TextLineHeight : 0;

  return {
    type: DocketDisplayItemType.ALLERGEN_BANNER,
    name,
    height:
      DocketDisplayItemTypeHeight[DocketDisplayItemType.ALLERGEN_BANNER] +
      heightAdjustment,
    docketId: docket.id,
  } as DocketDisplayItem;
};

export const constructTransferredLabel = (
  docketId: string,
  tableId: string,
  transferLabelType: DocketDisplayItemType,
): DocketDisplayItem => {
  const transferred = {
    docketId: docketId,
    type: transferLabelType,
    name: tableId,
    height: DocketDisplayItemTypeHeight[transferLabelType],
  };
  return transferred;
};

export const constructTransferredItems = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session: Session,
  orderTypes?: OrderType[],
  categories?: Category[],
  removableCourseIds?: Set<string>,
): DocketDisplayItem[] => {
  const transfers = groupTransfers(docket.docketItems);
  const transferredItems: DocketDisplayItem[] = [];

  transfers.forEach(docketItemsGroup => {
    // If every item in this transfer group belongs to a collapsed course, suppress the
    // banner on the docket card entirely — it stays visible in the side drawer only,
    // which renders from the unfiltered item list.
    const visibleGroupItems = filterOutRemovedCourseItems(
      docketItemsGroup,
      removableCourseIds || new Set(),
    );
    if (!visibleGroupItems.length) return;

    const transferLabel = resolveTransferLabel(docketItemsGroup[0]);
    if (!transferLabel) return;

    transferredItems.push(
      constructTransferredLabel(
        docket.id,
        transferLabel.tableId,
        transferLabel.transferLabelType,
      ),
    );

    docket.docketItems = docketItemsGroup;
    transferredItems.push(
      ...constructDisplayDocketItems(
        docket,
        assignedProfiles,
        session,
        orderTypes,
        categories,
        removableCourseIds,
      ),
    );
  });

  return transferredItems;
};

export const constructDueAtTime = (docket: Docket): DocketDisplayItem => {
  const result = {
    type: DocketDisplayItemType.DUE_AT,
    name: docket.dueAt + '',
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.DUE_AT],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return result;
};

export const constructDisplayDocketRecalledLabel = (
  docket: Docket,
): DocketDisplayItem => {
  const recallLabel = {
    type: DocketDisplayItemType.RECALLED_TAG,
    name: RECALLED_TEXT,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.RECALLED_TAG],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return recallLabel;
};

export const constructDisplayDocketVoidedStatus = (
  docket: Docket,
): DocketDisplayItem => {
  const voidLabel = {
    type: DocketDisplayItemType.VOID,
    name: VOIDED_TEXT,
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.VOID],
    docketId: docket.id,
    time: docket.createdAt,
  };
  return voidLabel;
};

const constructModifiers = (
  modifiers?: DocketModifierEntityInput[],
): DocketDisplayModifierItem[] => {
  return (modifiers ?? []).map(modifier => {
    let removeDefault = modifier.removeDefault;
    let name = '';
    if (modifier.quantity && modifier.quantity > 1)
      name = modifier.quantity + ' ✗ ' + modifier.name;
    else name = modifier.name || '';
    if (modifier.isDefault && modifier.quantity === 1) {
      removeDefault = true;
    }
    return { name, removeDefault };
  });
};

export const getItemNameLines = (name: string) =>
  Math.min(getNoOfLinesForText(name), ItemNameMaxLines);

export const getItemNameHeightAdjustment = (name: string) => {
  const lines = getItemNameLines(name);
  return lines > 1 ? lines * TextLineHeight : 0;
};

export const getNotesHeight = (notes: string) => {
  if (!notes) return 0;
  const numberOfLinesForNotes = Math.min(getNoOfLinesForText(notes), 3);
  const heightAdjustment =
    numberOfLinesForNotes > 1 ? numberOfLinesForNotes * TextLineHeight : 0;
  return (
    DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM + 'NOTE'] +
    heightAdjustment
  );
};

export const constructDisplayDocketItems = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session?: Session,
  orderTypes?: OrderType[],
  categories?: Category[],
  removableCourseIds?: Set<string>,
): DocketDisplayItem[] => {
  // Track which headers have been added
  const groupsAppended: string[] = []; // For top-level groups (seats, categories)
  const coursesAppended: Record<string, string[]> = {}; // For courses within groups, keyed by parent

  // Track course completion status (drives CourseCallHeader's timer-freeze only —
  // unrelated to course removal). The timer freezes once the kitchen has responded
  // (first-stage BUMPED), regardless of rebumpToggle — waiting for the second-stage
  // REBUMPED confirmation here would leave it ticking through the whole expo wait.
  const allDocketItems = docket?.docketItems || [];
  const courseItems = groupItemsByCourseId(allDocketItems);
  const courseCompletionStatus = new Map<string, boolean>();
  courseItems.forEach((items, courseId) => {
    courseCompletionStatus.set(courseId, isFullyBumped(items, false));
  });

  let seatName = '';
  let seatManagement = false;
  let orderTypeCode: OrderTypeCode | undefined;
  if (orderTypes?.length) {
    orderTypeCode = getOrderTypeCode(
      docket.orderType.id,
      orderTypes,
    ) as OrderTypeCode;
    seatManagement = session?.enableSeatManagement || false;
  }

  const profile = assignedProfiles.find(p => p.id === docket.printerProfileId);
  const groupingType = profile?.grouping?.groupBy;
  const displayStyle = profile?.grouping?.displayStyle;
  const isGroupingEnabled = profile?.grouping?.enabled;

  const categoryMap = new Map<string, string>();
  if (categories?.length) {
    categories.forEach(category => {
      categoryMap.set(category.id, category.name);
    });
  }

  const docketItems = filterOutRemovedCourseItems(
    docket?.docketItems || [],
    removableCourseIds || new Set(),
  );

  return docketItems
    .map((docketItem: DocketItem, index: number) => {
      const docketDisplayItem: DocketDisplayItem[] = [];
      // Handle seat management for Dine In
      if (
        (isGroupingEnabled && groupingType === GroupBy.SEAT) ||
        (seatManagement && orderTypeCode === OrderTypeCode.DINE_IN)
      ) {
        seatName = docketItem?.seatNumber
          ? `S${docketItem?.seatNumber?.toString() || ''}`
          : DEFAULT_TABLE_ABBREVIATION;
      }

      // For primary course grouping
      if (
        isGroupingEnabled &&
        docketItem?.course?.id &&
        groupingType === GroupBy.COURSE
      ) {
        const courseName = docketItem?.course?.name || '';

        // Check if course has been added
        if (!groupsAppended.includes(courseName)) {
          // Add to tracking
          groupsAppended.push(courseName);

          const course = {
            type: DocketDisplayItemType.COURSE,
            name: courseName,
            height:
              displayStyle === GroupDisplayStyle.HIDDEN
                ? 0
                : DocketDisplayItemTypeHeight[DocketDisplayItemType.COURSE],
            docketId: docket.id,
            time: docket.createdAt,
            primaryGrouping: true,
            displayPreference: {
              displayStyle,
            },
            orderTypeCode: orderTypeCode,
            courseCallInfo: docketItem?.course?.callInfo,
            areAllItemsBumped:
              courseCompletionStatus.get(docketItem?.course?.id || '') || false,
          };

          if (displayStyle !== GroupDisplayStyle.HIDDEN) {
            docketDisplayItem.push(course as DocketDisplayBaseItem);
          }
        }
      }

      // For seat grouping
      if (isGroupingEnabled && seatName && groupingType === GroupBy.SEAT) {
        // Add seat header if needed
        if (!groupsAppended.includes(seatName)) {
          // Track this seat
          groupsAppended.push(seatName);

          // Add to display
          const seatHeader = {
            type: DocketDisplayItemType.SEAT_NUMBER,
            name: seatName,
            height:
              DocketDisplayItemTypeHeight[DocketDisplayItemType.SEAT_NUMBER],
            docketId: docket.id,
            primaryGrouping: true,
            displayPreference: {
              displayStyle,
            },
          };

          if (
            displayStyle !== GroupDisplayStyle.HIDDEN &&
            orderTypeCode === OrderTypeCode.DINE_IN &&
            seatManagement
          ) {
            docketDisplayItem.push(seatHeader as DocketDisplayBaseItem);
          }

          // Initialize tracking for courses in this seat
          coursesAppended[seatName] = [];
        }

        // Add course header if needed
        if (docketItem?.course?.id) {
          const courseName = docketItem?.course?.name || '';

          // Initialize the array for this seat if it doesn't exist yet
          if (!coursesAppended[seatName]) {
            coursesAppended[seatName] = [];
          }

          // Only add if we haven't seen this course in this seat before
          if (!coursesAppended[seatName].includes(courseName)) {
            // Track this course for this seat
            coursesAppended[seatName].push(courseName);

            // Add to display
            const courseHeader = {
              type: DocketDisplayItemType.COURSE,
              name: courseName,
              height: DocketDisplayItemTypeHeight[DocketDisplayItemType.COURSE],
              docketId: docket.id,
              time: docket.createdAt,
              primaryGrouping: false,
              orderTypeCode: orderTypeCode,
              courseCallInfo: docketItem?.course?.callInfo,
              areAllItemsBumped:
                courseCompletionStatus.get(docketItem?.course?.id || '') ||
                false,
            };

            docketDisplayItem.push(courseHeader as DocketDisplayBaseItem);
          }
        }
      }

      // For category grouping
      if (
        isGroupingEnabled &&
        docketItem?.product &&
        categories &&
        groupingType === GroupBy.CATEGORY
      ) {
        const categoryId = docketItem?.product?.category?.id;
        const categoryName = categoryId
          ? categoryMap.get(categoryId) || ''
          : '';

        // Add category header if needed
        if (!groupsAppended.includes(categoryName)) {
          // Track this category
          groupsAppended.push(categoryName);

          // Add to display
          const categoryHeader = {
            type: DocketDisplayItemType.CATEGORY,
            name: categoryName || '',
            height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CATEGORY],
            docketId: docket.id,
            primaryGrouping: true,
            displayPreference: {
              displayStyle,
            },
          };

          if (
            categoryName?.length > 0 &&
            displayStyle !== GroupDisplayStyle.HIDDEN
          ) {
            docketDisplayItem.push(categoryHeader as DocketDisplayBaseItem);
          }

          // Initialize tracking for courses in this category
          coursesAppended[categoryName] = [];
        }

        // Add course header if needed
        if (docketItem?.course?.id) {
          const courseName = docketItem?.course?.name || '';

          // Initialize the array for this category if it doesn't exist yet
          if (!coursesAppended[categoryName]) {
            coursesAppended[categoryName] = [];
          }

          // Only add if we haven't seen this course in this category before
          if (!coursesAppended[categoryName].includes(courseName)) {
            // Track this course for this category
            coursesAppended[categoryName].push(courseName);

            // Add to display
            const courseHeader = {
              type: DocketDisplayItemType.COURSE,
              name: courseName,
              height: DocketDisplayItemTypeHeight[DocketDisplayItemType.COURSE],
              docketId: docket.id,
              orderTypeCode: orderTypeCode,
              time: docket.createdAt,
              primaryGrouping: false,
              courseCallInfo: docketItem?.course?.callInfo,
              areAllItemsBumped:
                courseCompletionStatus.get(docketItem?.course?.id || '') ||
                false,
            };

            docketDisplayItem.push(courseHeader as DocketDisplayBaseItem);
          }
        }
      }

      const displayName = getDocketItemDisplayName(docketItem);
      const numberOfLinesForProduct = getItemNameLines(displayName);
      const item: DocketDisplayOrderItem = {
        type: DocketDisplayItemType.ITEM,
        name: displayName,
        quantity: docketItem?.quantity || 0,
        height:
          DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM] +
          getItemNameHeightAdjustment(displayName),
        docketId: docket.id,
        id: docketItem.id,
        itemStatus: docketItem.itemStatus,
        numberOfLinesForProduct: numberOfLinesForProduct,
        orderItemId: docketItem.orderItemId || '',
        ...(seatManagement &&
          orderTypeCode === OrderTypeCode.DINE_IN &&
          (!isGroupingEnabled || groupingType !== GroupBy.SEAT) && {
            seatName: seatName.length > 0 ? seatName : undefined,
          }),
        consolidatedOrderItemIds: docketItem.consolidatedOrderItemIds,
        ...(docketItem?.course && {
          course: {
            id: docketItem.course.id,
            name: docketItem.course.name,
            priority: docketItem.course.priority,
            callInfo: docketItem.course.callInfo,
          },
        }),
        ...(hasAllergens(docketItem.allergens) && {
          allergens: docketItem.allergens,
          ...(docketItem.seatNumber
            ? { allergenSeatName: `S${docketItem.seatNumber}` }
            : {}),
        }),
      };
      if (hasAllergens(docketItem.allergens)) {
        item.height += getAllergenLabelsHeight(docketItem);
      }
      if (docketItem.notes) {
        const numberOfLinesForNotes = Math.min(
          getNoOfLinesForText(docketItem?.notes),
          3,
        );
        item['note'] = docketItem.notes;
        item['height'] = item['height'] + getNotesHeight(docketItem.notes);
        item['numberOfLinesForNotes'] = numberOfLinesForNotes;
      }
      if (docketItem.comboItems?.length) {
        const comboItems = docketItem.comboItems.map(cItem => ({
          name: cItem.product?.name || '',
          notes: cItem.notes || '',
          modifiers: constructModifiers(cItem.modifiers),
        })) as DocketDisplayComboItem[];
        // Total combo items height
        const totalHeight = comboItems.reduce((total, cItem) => {
          return (
            total +
            DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM] +
            (cItem?.notes ? getNotesHeight(cItem.notes) : 0) +
            (cItem.modifiers?.length
              ? DocketDisplayItemTypeHeight[
                  DocketDisplayItemType.ITEM + 'MODIFIER'
                ] * cItem.modifiers.length
              : 0)
          );
        }, 0);
        item['height'] = item['height'] + totalHeight;
        item.comboItems = comboItems;
      }
      if (docketItem?.modifiers?.length) {
        const modifiers = constructModifiers(docketItem?.modifiers);
        item['modifiers'] = modifiers;
        item['height'] =
          item['height'] +
          DocketDisplayItemTypeHeight[DocketDisplayItemType.ITEM + 'MODIFIER'] *
            modifiers.length;
      }

      docketDisplayItem.push(item);
      if (
        docket.status == DocketStatus.COMPLETED &&
        index == docketItems.length - 1 &&
        docket.itemStatus !== DocketItemStatus.VOIDED
      ) {
        const recall = {
          id: docket.id,
          docketId: docket.id,
          time: docket.createdAt,
          type: DocketDisplayItemType.RECALL_BUTTON,
          name: RECALL_TEXT,
          height:
            DocketDisplayItemTypeHeight[DocketDisplayItemType.RECALL_BUTTON],
        };
        docketDisplayItem.push(recall);
      }
      return docketDisplayItem;
    })
    .flat();
};

export const constructNotifiers = (docket: Docket): DocketDisplayItem => {
  const result = {
    type: DocketDisplayItemType.OTHER_ITEMS,
    name:
      docket.notifierItems && docket.notifierItems?.length > 1
        ? translate('kitchenDisplay.otherItems')
        : translate('kitchenDisplay.otherItem'),
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.OTHER_ITEMS],
    items: docket.docketItems,
    notifierItems: docket.notifierItems || [],
    docketId: docket.id,
    orderNumber: docket.orderNumber,
    status: docket.status,
    printerProfileId: docket.printerProfileId,
    orderTypeId: docket.orderType.id,
    allergens: docket.allergens,
  } as DocketDisplayOtherItem;
  return result;
};

export const constructViewAllCourses = (
  docket: Docket,
  allDocketItems: DocketItem[],
  policy: CourseCompletionPolicy,
): DocketDisplayItem | null => {
  const removedCourses = getRemovedCourses(allDocketItems, policy);
  if (!removedCourses.length) return null;
  return {
    type: DocketDisplayItemType.VIEW_ALL_COURSES,
    name: formatRemovedCourseNames(removedCourses),
    height: DocketDisplayItemTypeHeight[DocketDisplayItemType.VIEW_ALL_COURSES],
    items: allDocketItems,
    notifierItems: docket.notifierItems || [],
    removedCourses,
    docketId: docket.id,
    orderNumber: docket.orderNumber,
    status: docket.status,
    printerProfileId: docket.printerProfileId,
    orderTypeId: docket.orderType.id,
  } as DocketDisplayItem;
};

export const transformDocketToDocketDisplayItems = (
  docket: Docket,
  assignedProfiles: PrinterProfile[],
  session: Session,
  orderTypes: OrderType[],
  categories: Category[],
  currentIndex?: number,
  totalCount?: number,
  collapsedNotifiers: Record<string, boolean> = {},
): DocketDisplayItem[] => {
  if (!docket) return [] as DocketDisplayItem[];
  // Captured before docket.docketItems is reassigned below, so course removability is
  // computed once over the full ticket, not a partial subset.
  const allDocketItems = docket.docketItems;
  const orderTypeCodeForCourseCall = orderTypes?.length
    ? (getOrderTypeCode(docket.orderType.id, orderTypes) as OrderTypeCode)
    : undefined;
  const isCourseCallApplicable = resolveIsCourseCallApplicable(
    orderTypeCodeForCourseCall,
    session,
  );
  const courseCompletionPolicy: CourseCompletionPolicy = {
    isCourseCallApplicable,
    rebumpToggle: !!session?.rebumpToggle,
  };
  // Course removal only applies to the active docket. A COMPLETED docket's items are
  // already all "done", so computing removability unscoped could empty out
  // docketItems entirely, and with it the recall button, which is anchored to the last
  // rendered item below.
  const isActiveDocket = docket.status === DocketStatus.CREATED;
  const removableCourseIds = isActiveDocket
    ? getRemovableCourseIds(allDocketItems, courseCompletionPolicy)
    : new Set<string>();
  const [notTransferred, transferred] = partition(
    docket.docketItems,
    item => !item.transferInfo?.fromOrderId || !item.transferInfo.toOrderId,
  );
  docket.docketItems = notTransferred;
  let displayDocket: DocketDisplayItem[] = [];
  const seatedGuests = docket.seatedGuests ?? 0;
  if (docket.tags?.length || docket.rushedAt || totalCount || seatedGuests)
    displayDocket.push(
      constructDisplayDocketTitleBar(docket, currentIndex, totalCount),
    );
  if (docket.isTraining)
    displayDocket.push(constructDisplayTrainingHeader(docket));
  displayDocket.push(...constructDisplayDocketHeader(docket));
  if (docket.status == DocketStatus.CREATED && docket.dueAt) {
    displayDocket.push(constructDueAtTime(docket));
  }
  if (docket.notes || docket.customFieldResponses?.length)
    displayDocket = [...displayDocket, constructDisplayDocketNotes(docket)];
  const allergenBanner = constructAllergenBanner(docket);
  if (allergenBanner) displayDocket = [...displayDocket, allergenBanner];
  if (docket.itemStatus == DocketItemStatus.VOIDED)
    displayDocket = [
      ...displayDocket,
      constructDisplayDocketVoidedStatus(docket),
    ];
  if (docket.itemStatus == DocketItemStatus.RECALLED)
    displayDocket = [
      ...displayDocket,
      constructDisplayDocketRecalledLabel(docket),
    ];
  if (docket?.docketItems?.length)
    displayDocket.push(
      ...constructDisplayDocketItems(
        docket,
        assignedProfiles,
        session,
        orderTypes,
        categories,
        removableCourseIds,
      ),
    );
  if (transferred?.length) {
    docket.docketItems = transferred;
    displayDocket = [
      ...displayDocket,
      ...constructTransferredItems(
        docket,
        assignedProfiles,
        session,
        orderTypes,
        categories,
        removableCourseIds,
      ),
    ];
  }
  if (docket?.notifierItems && docket?.notifierItems?.length > 0) {
    // docket.docketItems was reassigned above (first to notTransferred, then to
    // transferred, if any) — restore the full list before constructNotifiers reads it,
    // otherwise the notifiers drawer shows only the transferred items (OC-6615).
    docket.docketItems = allDocketItems;
    displayDocket = session?.kitchenDisplay?.showNotifiersInline
      ? [
          ...displayDocket,
          ...constructNotifierRows(
            docket,
            assignedProfiles,
            session,
            collapsedNotifiers,
          ),
        ]
      : [...displayDocket, constructNotifiers(docket)];
  }
  const viewAllCourses = isActiveDocket
    ? constructViewAllCourses(docket, allDocketItems, courseCompletionPolicy)
    : null;
  if (viewAllCourses) {
    displayDocket = [...displayDocket, viewAllCourses];
  }
  return displayDocket;
};

export const addContinueTags = (displayDockets: DocketDisplayItem[]) => {
  let height = HeaderHeight;
  displayDockets.forEach((displayDocket: DocketDisplayItem, i: number) => {
    height = height + displayDocket?.height || 0;
    const isNotHeader =
      displayDocket?.type != DocketDisplayItemType.DOCKET_HEADER;
    const heightOfDocketView = theme.window.vh - FooterHeight;
    const remianingSpaceHeight = height - heightOfDocketView;
    if (isNotHeader && remianingSpaceHeight > 0) {
      let index = i;
      if (
        remianingSpaceHeight <
        DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE]
      )
        index = i - 1;
      height =
        HeaderHeight +
        DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE];
      displayDockets.splice(
        index,
        0,
        {
          type: DocketDisplayItemType.CONTINUE,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
        },
        {
          type: DocketDisplayItemType.CONTINUED,
          name: CONTINUE,
          height: DocketDisplayItemTypeHeight[DocketDisplayItemType.CONTINUE],
        },
      );
    }
  });
  return displayDockets;
};

export const getOrderTypeName = (id: string, orderTypes: OrderType[]) => {
  return orderTypes.find(orderType => orderType.id == id)?.name || '';
};

export const getCateringFulfillmentType = (
  orderTypeCode: string | undefined,
  fulfilmentDetails?: FulfilmentDetails,
): string => {
  if (orderTypeCode !== OrderTypeCode.CATERING) {
    return '';
  }
  const fulfilmentType = fulfilmentDetails?.catering?.type;

  if (fulfilmentType === OrderTypeCode.DELIVERY) {
    return ` - ${translate('kitchenDisplay.delivery')}`;
  }
  if (fulfilmentType === OrderTypeCode.PICK_UP) {
    return ` - ${translate('kitchenDisplay.pickUp')}`;
  }
  return '';
};

export const getOrderTypeCode = (id: string, orderTypes: OrderType[]) => {
  return orderTypes.find(orderType => orderType.id == id)?.code;
};

export const getPartnerSalesChannelName = (
  id: string,
  salesChannels: SalesChannel[],
) => {
  return (
    salesChannels
      .filter(salesChannel => {
        if (
          salesChannel.name == SALES_CHANNEL.INSTORE ||
          salesChannel.name.toUpperCase() == SALES_CHANNEL.ONLINE
        )
          return false;
        return true;
      })
      .find(salesChannel => salesChannel.id == id)?.name || ''
  );
};

export const getDockerHeaderTitle = (props: {
  orderTypes: OrderType[];
  orderTypeId: string;
  tableName: string;
  sectionName: string;
  customerName: string;
  orderNumber: string;
  source: string;
}): string => {
  if (props.source === App.KIOSK) {
    return (
      props.customerName?.trim() ||
      String(+props.orderNumber.split('-')?.splice(1, 1))
    );
  }

  const DineIn = props.orderTypes.find(
    orderType => orderType.code === OrderTypeCode.DINE_IN,
  );

  if (DineIn && DineIn.id === props.orderTypeId) {
    // Show Table Details for Dine In Orders
    if (props.sectionName) {
      return [props.sectionName, props.tableName].filter(x => x).join(', ');
    }

    if (props.tableName) {
      return props.tableName;
    }
  }

  if (props.customerName) {
    // Show Customer Name Otherwise
    return capitalizeWords(props.customerName.trim().toLowerCase());
  }

  return DEFAULT_DOCKET_NAME;
};

export const getHeaderColorByOrderType = (
  orderTypeId: string,
  orderTypes: OrderType[],
): string => {
  if (!orderTypeId) return theme.colors.primary;
  const currentOrderType = orderTypes.find(
    orderType => orderType.id == orderTypeId,
  );
  if (currentOrderType && DOCKET_HEADER_COLOR[`${currentOrderType.code}`])
    return DOCKET_HEADER_COLOR[`${currentOrderType.code}`];
  return theme.colors.teal;
};

export const getNoOfLinesForText = (input: string) => {
  if (!input || input.length === 0) return 1;

  return input.split('\n').reduce((totalLines, line) => {
    return (
      totalLines + Math.max(1, Math.ceil(line.length / TextCharacterCount))
    );
  }, 0);
};
